import {
  EditMessageThreadRemoveTagsMutation,
  EditMessageThreadRemoveTagsMutationVariables,
  MessageThreadFragment,
  TagFragment,
} from "gql/graphql";

import Pill from "modules/_shared/pill";
import React, { memo, useCallback, useMemo } from "react";
import { HASH_KEY } from "modules/utils/Constants";
import { useMutation } from "@apollo/client";
import {
  MIXPANEL_EVENT_TYPE,
  useMixpanel,
} from "modules/providers/mixpanel-provider/mixpanel-provider";
import EDIT_MESSAGE_THREAD_REMOVE_TAGS_MUTATION from "../_networking/edit-message-thread-remove-tags";
import { useAuth } from "modules/providers/auth-provider";
import useRemovedFromThread from "modules/hooks/use-removed-from-thread";
import { isMessageThreadReadOnly } from "modules/utils/Functions";

const TagActionablePill: React.FC<{
  messageThread: MessageThreadFragment;
  tag: TagFragment;
}> = ({ messageThread, tag }) => {
  const { me } = useAuth();
  const { track } = useMixpanel();

  const [editMessageThreadRemoveTagsAPI] = useMutation<
    EditMessageThreadRemoveTagsMutation,
    EditMessageThreadRemoveTagsMutationVariables
  >(EDIT_MESSAGE_THREAD_REMOVE_TAGS_MUTATION);

  const onRemoveTagClicked = useCallback(
    (tagId: string) => {
      editMessageThreadRemoveTagsAPI({
        variables: {
          id: messageThread.id,
          tagIdList: [tagId],
        },
      });
      track(MIXPANEL_EVENT_TYPE.TAG_REMOVED);
    },
    [editMessageThreadRemoveTagsAPI, messageThread.id, track],
  );

  const { isMeRemoved } = useRemovedFromThread(messageThread);

  const canRemoveTag = useMemo(() => {
    if (!me) return false;
    if (isMeRemoved) return false;
    if (
      isMessageThreadReadOnly(messageThread) &&
      !messageThread.createMessageUserIdList.includes(me.id)
    ) {
      return false;
    }
    return true;
  }, [isMeRemoved, me, messageThread]);

  return (
    <Pill
      id={tag.id}
      label={tag.displayName}
      isActive={tag.active}
      leftChar={HASH_KEY}
      onClose={canRemoveTag ? onRemoveTagClicked : undefined}
    />
  );
};

export default memo(TagActionablePill);
